import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { io, Socket } from "socket.io-client";
import { SOCKET_ENDPOINT } from "../constants/api.constants";
import { User, Message } from "../types";
import { queryClient } from "./queryClient";
import { queryKeys } from "./keys/queryKeys";
import { useChatStore } from "./chatStore";
import { useAuthStore } from "./authStore";
import { markChatNotificationsRead } from "./api/notificationApi";

/**
 * Socket Store — Zustand
 *
 * Manages the Socket.IO connection lifecycle:
 * - connect() called on login and by Chatpage on load (restores the socket
 *   after a page refresh) → one socket lives for the entire session
 * - disconnect() called on logout → cleans up listeners
 * - joinRoom() called when selectedChat changes
 *
 * Incoming socket events update:
 * - TanStack Query cache (new message → invalidate messages query)
 * - Zustand chatStore (typing indicators)
 * - Notifications query (refetch; mark read when the chat is open)
 *
 * This replaces the previous socketRef inside SingleChat.tsx.
 * The socket NO LONGER reconnects on every chat switch.
 */

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;

  connect: (user: User) => void;
  disconnect: () => void;
  joinRoom: (chatId: string) => void;
  emitTyping: (chatId: string) => void;
  emitStopTyping: (chatId: string) => void;
  emitNewMessage: (message: Message) => void;
  emitReaction: (message: Message) => void;
  emitMessageDeleted: (message: Message) => void;
}

export const useSocketStore = create<SocketState>()(
  devtools(
    (set, get) => ({
      socket: null,
      isConnected: false,

      connect: (user: User) => {
        // A socket already exists (connected or still connecting) — socket.io
        // reconnects it on its own; opening a second one would duplicate events
        if (get().socket) return;

        const socket = io(SOCKET_ENDPOINT);

        socket.on("connect", () => {
          socket.emit("setup", user);
          set({ socket, isConnected: true }, false, "socket/connect");
        });

        socket.on("disconnect", () => {
          set({ isConnected: false }, false, "socket/disconnect");
        });

        // ── Incoming message ─────────────────────────────────────────────────
        socket.on("message recieved", (newMessage: Message) => {
          const { selectedChat } = useChatStore.getState();
          const notificationsKey = queryKeys.notifications.list();

          if (selectedChat?._id === newMessage.chat._id) {
            // Active chat → invalidate TanStack Query cache so messages refetch
            queryClient.invalidateQueries({
              queryKey: queryKeys.messages.list(newMessage.chat._id),
            });
            // The server persisted a notification; the user is looking at this chat
            markChatNotificationsRead(newMessage.chat._id)
              .catch(() => undefined)
              .finally(() => queryClient.invalidateQueries({ queryKey: notificationsKey }));
          } else {
            // Background chat → refetch the persisted notifications for the bell
            queryClient.invalidateQueries({ queryKey: notificationsKey });
          }

          // Always invalidate chat list so latest message preview updates
          queryClient.invalidateQueries({ queryKey: queryKeys.chats.all() });
        });

        // ── Reaction update ──────────────────────────────────────────────────
        socket.on("message reaction", ({ message }) => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.messages.list(message.chat._id),
          });
        });

        // ── Message deleted ──────────────────────────────────────────────────
        socket.on("message deleted", ({ chat }) => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.messages.list(chat._id),
          });
        });

        // ── Typing indicators ────────────────────────────────────────────────
        socket.on("typing", (chatId: string) => {
          useChatStore.getState().setTyping(chatId, true);
        });

        socket.on("stop typing", (chatId: string) => {
          useChatStore.getState().setTyping(chatId, false);
        });

        set({ socket, isConnected: false }, false, "socket/initializing");
      },

      disconnect: () => {
        get().socket?.disconnect();
        set({ socket: null, isConnected: false }, false, "socket/disconnected");
      },

      joinRoom: (chatId: string) => {
        get().socket?.emit("join chat", chatId);
      },

      emitTyping: (chatId: string) => {
        get().socket?.emit("typing", chatId);
      },

      emitStopTyping: (chatId: string) => {
        get().socket?.emit("stop typing", chatId);
      },

      emitNewMessage: (message: Message) => {
        get().socket?.emit("new message", message);
      },

      emitReaction: (message: Message) => {
        const actorId = useAuthStore.getState().user?._id;
        if (!actorId) return;
        get().socket?.emit("message reaction", { message, actorId });
      },

      emitMessageDeleted: (message: Message) => {
        const actorId = useAuthStore.getState().user?._id;
        if (!actorId) return;
        get().socket?.emit("message deleted", {
          messageId: message._id,
          chat: { _id: message.chat._id, users: message.chat.users.map((u) => ({ _id: u._id })) },
          actorId,
        });
      },
    }),
    { name: "SocketStore" }
  )
);
